import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { Box, Paginator, Grid, Typography } from '@kis-technologies/kis-ui';
import PurchaseLogo from 'assets/purchase_icon.png';
import WillCallLogo from 'assets/will_call_icon.png';
import FoodDrinkLogo from 'assets/food-and-drink.png';
import CityPassLogo from 'assets/CityPASS_Logo.png';
import { ActionOverlay } from 'components/ActionOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { ArrowPagination } from 'components/Pagination';
import { CardContainer } from 'components/CardContainer';
import { TileLayout } from 'components/TileLayout';
import { useActions } from 'hooks/useActions';
import { actions as customerActions } from 'services/customer';
import { Configuration, selectors as configSelectors } from 'services/configuration';
import { actions as ticketingActions, selectors as ticketingSelectors } from 'services/ticketing';
import { actions as hardwareActions } from 'services/hardware';
import { useStyles } from './styled';

const getEnabledPaths = (navPaths: any): string[] => {
  if (navPaths) {
    return Object.keys(navPaths).reduce((previous, key) => {
      if (navPaths[key].enabled) {
        previous.push(key);
      }
      return previous;
    }, [] as string[]);
  }
  return ['ticketing'];
};

const getOrderedPaths = (navPaths: string[], tileOrder?: string[]): string[] => {
  let orderedPaths: string[] = [];
  const enabledPaths = [...navPaths];
  if (tileOrder && tileOrder.length) {
    tileOrder.forEach((tile) => {
      if (enabledPaths.includes(tile)) {
        orderedPaths = orderedPaths.concat(enabledPaths.splice(enabledPaths.indexOf(tile), 1));
      }
    });
  }
  orderedPaths = orderedPaths.concat(enabledPaths);

  return orderedPaths;
};

const StartPurchaseView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const state = location.state as { from?: string };

  const {
    resetMembership,
    resetScanner,
    createSession,
    resetSession,
    resetAppliedPriceTypePromo,
    setConfigPath,
    setNavigationPath,
    resetCustomer
  } = useActions({
    resetMembership: ticketingActions.resetMembership,
    resetScanner: hardwareActions.resetScanner,
    createSession: ticketingActions.createSession,
    resetSession: ticketingActions.resetSession,
    resetAppliedPriceTypePromo: ticketingActions.resetAppliedPriceTypePromo,
    setConfigPath: ticketingActions.setConfigPath,
    setNavigationPath: ticketingActions.setNavigationPath,
    resetCustomer: customerActions.resetCustomer
  });
  const session = useSelector(ticketingSelectors.selectSession);
  const loading = useSelector(ticketingSelectors.selectLoading);
  const ticketingError = useSelector(ticketingSelectors.selectError);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;

  const fontFamily = get(configuration, 'configuration.globalSettings.fonts.primary', '');
  const membershipPrompt = get(configuration, 'ticketing.membershipLookup.enabled');
  const membershipPromptHeader = get(
    configuration,
    `ticketing.membershipLookup.messages.header.${i18n.language}`,
    t('startPurchase.memberSelectionHeader')
  );
  const isLocationValidationEnabled = get(configuration, 'ticketing.locationValidation.enabled', false);
  const overlayMessages = get(configuration, 'ticketing.locationValidation.overlayMessages', {});
  const [locationOverlayHeader, locationOverlayCancelButton, locationOverlayConfirmButton] = [
    get(overlayMessages, `header.${i18n.language}`, t('startPurchase.locationValidationHeader')),
    get(overlayMessages, `buttons.${i18n.language}[0]`, t('commonText.yes')),
    get(overlayMessages, `buttons.${i18n.language}[1]`, t('commonText.no'))
  ];
  const flowTemplate = get(configuration, 'configuration.flowTemplate');
  const navigationSelection = get(configuration, 'configuration.navigationSelection');
  const touchForward = get(configuration, 'configuration.globalSettings.touchForward.general.enabled');
  const enabledPaths = getEnabledPaths(navigationSelection);
  const orderedPaths = getOrderedPaths(enabledPaths, navigationSelection && navigationSelection.tileOrder);
  const autoSelectPath = orderedPaths.length === 1 ? orderedPaths[0] : '';

  const startPurchaseHeader = get(
    navigationSelection,
    `messages.header.${i18n.language}`,
    t('startPurchase.startPurchaseHeader')
  );
  const purchaseDesc = get(
    navigationSelection,
    `ticketing.messages.header.${i18n.language}`,
    t('startPurchase.purchaseTileHeader')
  );
  const printDesc = get(
    navigationSelection,
    `willCall.messages.header.${i18n.language}`,
    t('startPurchase.printTileHeader')
  );
  const infoDesc = get(navigationSelection, `info.messages.header.${i18n.language}`, t('startPurchase.infoTileHeader'));
  const cityPassDesc = get(
    navigationSelection,
    `cityPass.messages.header.${i18n.language}`,
    t('startPurchase.cityPassHeader')
  );
  const donationsDesc = get(
    navigationSelection,
    `donations.messages.header.${i18n.language}`,
    t('startPurchase.donationTileHeader')
  );

  const [sessionFlag, setSessionFlag] = useState(false);
  const [showMembershipOverlay, setShowMembershipOverlay] = useState(false);
  const [showLocationOverlay, setShowLocationOverlay] = useState(false);
  const [selectedPath, setSelectedPath] = useState(autoSelectPath);
  const [uiLoading, setUILoading] = React.useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const classes = useStyles({ fontFamily })();

  useEffect(() => {
    resetSession();
    resetScanner();
    resetMembership();
    resetAppliedPriceTypePromo();
    setNavigationPath(null);
    resetCustomer();

    const followingPages = ['/ticketing/genres', '/willcall/selection', '/ticketing/info', '/ticketing/citypass'];
    if (autoSelectPath && state && followingPages.includes(state?.from ?? '')) {
      navigate('/ticketing/attract');
    } else {
      if (selectedPath && (touchForward || autoSelectPath)) {
        if (selectedPath && selectedPath === 'info') {
          navigate('/ticketing/info');
        } else if (selectedPath && selectedPath === 'cityPass') {
          navigate('/ticketing/citypass');
        } else {
          createSession();
          setSessionFlag(true);
        }
      } else {
        setUILoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPath]);

  // Once the session is created, navigate to the next page
  useEffect(() => {
    if (session && sessionFlag) {
      checkMembership();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sessionFlag]);

  // Check session error and if its fair flow navigate to attract loop
  useEffect(() => {
    if (flowTemplate === 'fair' && ticketingError) {
      navigate('/ticketing/attract');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketingError]);

  // Show the location overlay if enabled
  const checkLocationValidation = () => {
    if (isLocationValidationEnabled) {
      if (showMembershipOverlay) {
        setUILoading(true);
        setShowMembershipOverlay(false);
        setShowLocationOverlay(true);
        setTimeout(() => setUILoading(false), 100);
      } else {
        setShowLocationOverlay(true);
        setUILoading(false);
      }
    } else {
      goToNextPage();
    }
  };

  // Show the membership overlay if enabled
  const checkMembership = () => {
    setNavigationPath(selectedPath);
    const showPrompts = selectedPath === 'ticketing' || selectedPath === 'donations';

    if (membershipPrompt && showPrompts) {
      setShowMembershipOverlay(true);
      setUILoading(false);
    } else if (showPrompts) {
      checkLocationValidation();
    } else {
      goToNextPage();
    }
  };

  const goToNextPage = () => {
    if (selectedPath === 'ticketing') {
      setConfigPath('ticketing');
      if (flowTemplate === 'fair') {
        navigate('/ticketing/selection');
      } else {
        navigate('/ticketing/genres');
      }
    } else if (selectedPath === 'willCall') {
      navigate('/willcall/selection');
    } else if (selectedPath === 'donations') {
      navigate('/ticketing/donation');
    } else {
      setUILoading(false);
    }
  };

  const nextButtonClick = () => {
    if (selectedPath) {
      if (selectedPath && selectedPath === 'info') {
        navigate('/ticketing/info');
      } else if (selectedPath && selectedPath === 'cityPass') {
        navigate('/ticketing/citypass');
      } else {
        createSession();
        setSessionFlag(true);
      }
    }
  };

  const goToMembershipLookup = () => {
    navigate('/ticketing/membership-lookup');
  };

  const goToLocationLookup = () => {
    navigate('/ticketing/location-lookup');
  };

  const onPrevPageClick = () => {
    navigate('/ticketing/attract');
  };

  const getOverlayTitle = (header: string) => (
    <Box margin="0 0 5">
      <Typography variant="h2" className={classes.overlayHeader}>
        {header}
      </Typography>
    </Box>
  );

  const getNavigationTiles = () => (
    <Paginator
      paginationComponent={ArrowPagination}
      items={orderedPaths}
      itemsPerPage={3}
      page={currentPage}
      onPageChange={(newPage) => setCurrentPage(newPage)}
    >
      {({ items }) => (
        <Grid className={classes.cardContainer} container>
          {items.map((path) => {
            switch (path) {
              case 'ticketing':
                /* Card for Purchase Flow */
                return (
                  <CardContainer
                    key={path}
                    imgSrc={PurchaseLogo}
                    descText={purchaseDesc}
                    testId="purchase-ticket-button"
                    cardAction={() => setSelectedPath('ticketing')}
                    isSelected={selectedPath === 'ticketing'}
                  />
                );
              case 'willCall':
                /* Card for Will Call Flow */
                return (
                  <CardContainer
                    key={path}
                    imgSrc={WillCallLogo}
                    descText={printDesc}
                    testId="willcall-button"
                    cardAction={() => setSelectedPath('willCall')}
                    isSelected={selectedPath === 'willCall'}
                  />
                );
              case 'info':
                /* Card for Informational Screen Flow */
                return (
                  <CardContainer
                    key={path}
                    imgSrc={FoodDrinkLogo}
                    descText={infoDesc}
                    testId="info-button"
                    cardAction={() => setSelectedPath('info')}
                    isSelected={selectedPath === 'info'}
                  />
                );
              case 'cityPass':
                /* Card for CityPASS Flow */
                return (
                  <CardContainer
                    key={path}
                    imgSrc={CityPassLogo}
                    descText={cityPassDesc}
                    testId="citypass-button"
                    cardAction={() => setSelectedPath('cityPass')}
                    isSelected={selectedPath === 'cityPass'}
                  />
                );
              case 'donations':
                /* Card for Donation Flow */
                return (
                  <CardContainer
                    key={path}
                    imgSrc={PurchaseLogo}
                    descText={donationsDesc}
                    testId="donations-button"
                    cardAction={() => setSelectedPath('donations')}
                    isSelected={selectedPath === 'donations'}
                  />
                );
              default:
                return null;
            }
          })}
        </Grid>
      )}
    </Paginator>
  );

  if (loading || uiLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      {!showMembershipOverlay && !showLocationOverlay && (
        <>
          <TileLayout headerText={startPurchaseHeader}>{getNavigationTiles()}</TileLayout>
          <Footer
            onPrevClick={onPrevPageClick}
            prevText={t('commonText.back')}
            isNextDisabled={!selectedPath}
            onNextClick={nextButtonClick}
            nextText={touchForward ? undefined : t('commonText.next')}
          />
        </>
      )}
      {/* Membership lookup overlay */}
      {showMembershipOverlay && (
        <ActionOverlay
          title={getOverlayTitle(membershipPromptHeader)}
          cancelText={t('commonText.yes')}
          confirmText={t('commonText.no')}
          onCancel={goToMembershipLookup}
          onConfirm={checkLocationValidation}
          height={500}
        />
      )}
      {/* Location lookup overlay */}
      {showLocationOverlay && (
        <ActionOverlay
          title={getOverlayTitle(locationOverlayHeader)}
          cancelText={locationOverlayCancelButton}
          confirmText={locationOverlayConfirmButton}
          onCancel={goToLocationLookup}
          onConfirm={goToNextPage}
          height={500}
        />
      )}
    </>
  );
};

export default StartPurchaseView;
